import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr, fdt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, moduleOn } from "@/lib/perms";
import { ConsultBillingQueue } from "@/components/pawkt/ConsultBillingQueue";
import { ReceptionWorkQueue } from "@/components/pawkt/ReceptionWorkQueue";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { CalendarDays, UserPlus, PawPrint, FileText, Search, CheckCircle2, Clock, ShieldCheck, Wallet } from "lucide-react";

const STATUS_TONE = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700", expired: "bg-slate-200 text-slate-600" };

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="rounded-2xl border border-pawkt-border bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-pawkt-text-secondary text-xs font-semibold uppercase tracking-wide">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 text-2xl font-display font-extrabold text-pawkt-text tabular">{value}</div>
    </div>
  );
}

export default function ReceptionDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["reception-dashboard"], queryFn: async () => (await api.get("/reception/dashboard")).data });
  const { data: mine } = useQuery({ queryKey: ["my-approvals"], queryFn: async () => (await api.get("/approvals?limit=50")).data });

  if (isLoading) return <Loading />;
  const d = data || {};
  const quick = [
    { label: "New Booking", icon: CalendarDays, to: "/bookings", perm: "bookings.create" },
    { label: "New Customer", icon: UserPlus, to: "/customers", perm: "customers.create" },
    { label: "Add Pet", icon: PawPrint, to: "/pets", perm: "pets.create" },
    { label: "Create Invoice", icon: FileText, to: "/pos", perm: "invoices.create" },
    { label: "Find Customer", icon: Search, to: "/customers", perm: "customers.view" },
  ].filter((q) => hasPerm(user, q.perm));

  return (
    <div>
      <PageHeader title={`Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`} subtitle="Your day at a glance" />

      <div className="flex flex-wrap gap-2 mb-5" data-testid="reception-quick-actions">
        {quick.map((q) => (
          <button key={q.label} onClick={() => navigate(q.to)} data-testid={`quick-${q.label.toLowerCase().replace(/\s/g, "-")}`}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800">
            <q.icon className="h-4 w-4" /> {q.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="reception-stats">
        <Stat label="Today's bookings" value={d.bookings_total ?? 0} icon={CalendarDays} testid="rc-bookings" />
        <Stat label="Upcoming" value={d.upcoming ?? 0} icon={Clock} testid="rc-upcoming" />
        <Stat label="Completed" value={d.completed ?? 0} icon={CheckCircle2} testid="rc-completed" />
        <Stat label="Invoices today" value={d.invoices_today ?? 0} icon={FileText} testid="rc-invoices" />
        {d.collections_today != null && <Stat label="Collections today" value={inr(d.collections_today)} icon={Wallet} testid="rc-collections" />}
        <Stat label="My pending requests" value={d.my_pending ?? 0} icon={ShieldCheck} testid="rc-pending" />
      </div>

      {hasPerm(user, "invoices.create") && moduleOn(user, "grooming") && <ReceptionWorkQueue />}
      {hasPerm(user, "invoices.create") && moduleOn(user, "vet") && <div className="mb-6"><ConsultBillingQueue /></div>}

      <div className="rounded-2xl border border-pawkt-border bg-white p-4" data-testid="my-requests">
        <div className="flex items-center gap-2 font-display font-bold text-pawkt-text mb-3"><ShieldCheck className="h-4 w-4 text-amber-500" /> My Approval Requests</div>
        {(mine?.items || []).length === 0 ? (
          <p className="text-sm text-pawkt-text-secondary">You haven't sent any approval requests.</p>
        ) : (
          <div className="space-y-2">
            {(mine.items || []).map((r) => (
              <div key={r.id} data-testid={`my-request-${r.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 capitalize">{r.action.replace(/[._]/g, " ")}</div>
                  <div className="text-xs text-slate-500 truncate">{r.target_label} · {fdt(r.created_at)}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
